import { h } from "./dom.ts";

export interface View { scale: number; x: number; y: number }
export const MAX_PHOTO_ZOOM = 4;

/** Keeps the photo between 1× and 4× and its edges from coming away from the frame. */
export function clampView(v: View, width: number, height: number): View {
  const scale = Math.min(MAX_PHOTO_ZOOM, Math.max(1, v.scale));
  const maxX = ((scale - 1) * width) / 2, maxY = ((scale - 1) * height) / 2;
  const clamp = (n: number, max: number): number => Math.min(max, Math.max(-max, n)) + 0; // + 0 turns -0 into 0
  return { scale, x: clamp(v.x, maxX), y: clamp(v.y, maxY) };
}

/**
 * Look closer: the photo full screen, pinch or drag to zoom and pan the photo itself.
 * `touch-action: none` on the photo keeps a pinch from zooming the whole page, which on iPhone
 * left the play screen zoomed in after Done. Double-tap or double-click toggles 2.5×.
 */
export function photoViewer(src: string, alt: string): HTMLDialogElement {
  const img = h("img", { src, alt, draggable: "false" });
  const frame = h("div", { class: "viewer-frame" }, img);
  const dialog = h("dialog", { class: "viewer", "aria-label": "The photo" },
    frame, h("form", { method: "dialog" }, h("button", { class: "primary small-button" }, "Done")));

  let view: View = { scale: 1, x: 0, y: 0 };
  const set = (v: View): void => {
    view = clampView(v, frame.clientWidth, frame.clientHeight);
    img.style.transform = view.scale === 1 ? "" : `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;
  };
  const pointers = new Map<number, { x: number; y: number }>();
  let pinch: { distance: number; scale: number } | undefined;
  const distance = (): number => {
    const [a, b] = [...pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };

  frame.addEventListener("pointerdown", (e) => {
    try { frame.setPointerCapture(e.pointerId); } catch { /* not an active pointer: carry on without capture */ }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) pinch = { distance: distance(), scale: view.scale };
  });
  frame.addEventListener("pointermove", (e) => {
    const last = pointers.get(e.pointerId);
    if (!last) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch && pointers.size === 2) {
      if (pinch.distance > 0) set({ ...view, scale: (pinch.scale * distance()) / pinch.distance });
    } else if (pointers.size === 1 && view.scale > 1) {
      set({ ...view, x: view.x + e.clientX - last.x, y: view.y + e.clientY - last.y });
    }
  });
  const lift = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = undefined;
  };
  frame.addEventListener("pointerup", lift);
  frame.addEventListener("pointercancel", lift);
  frame.addEventListener("dblclick", () => set(view.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 }));
  // A trackpad pinch arrives as a wheel event with ctrlKey.
  frame.addEventListener("wheel", (e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    set({ ...view, scale: view.scale * Math.exp(-e.deltaY / 100) });
  }, { passive: false });
  dialog.addEventListener("close", () => { pointers.clear(); pinch = undefined; set({ scale: 1, x: 0, y: 0 }); });
  return dialog;
}
